import { Components, ThisGorillaUtils } from "@thisgorilla/primate-ds";
import { AnimatePresence, motion } from "framer-motion";
import { createContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useClickOutside } from "../../../hooks/useClickOutside";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import {
  disableScroll,
  enableScroll,
  MAX_MODAL_HEIGHT,
  MODAL_Z_INDEX,
} from "../../../utils";
import { FlexRowCenter, FlexRowSpaced } from "../Styles";
import { ModalProps } from "./Modal";
import { useModalContentHeight } from "./useModalContentHeight";

export const ModalContext = createContext({} as ModalProps.Context);

export const Modal = ({
  children,
  type,
  stretchToFullWidth = type === "Onboarding" ? false : true,
  maxWidth = type === "Onboarding" ? "unset" : 726,
  minWidth = type === "Onboarding" ? "unset" : 320,
  maxHeight = "unset",
  minHeight = "unset",
  from,
  showCloseButton,
  setCloseButton,
  CloseButtonPosition = "right",
  onDrop,
  zIndex = MODAL_Z_INDEX,
  modalOpen = true,
  padding = 0,
  gap = 0,
  closeModalOnClickOutside = false,
  stretchToFullHeight = false,
  showCarousel,
  carouselIndex = 0,
  carouselMaxLength = 0,
  leftCarouselClick,
  rightCarouselClick,
  modalSize = "large",
  closeButtonTabIndex = 0,
}: ModalProps.IProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  //#region Data
  const modalRef = useRef<HTMLDivElement>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { maxContentHeight } = useModalContentHeight({
    headerRef,
    footerRef,
    gap,
    padding,
    maxHeight: maxHeight === "unset" ? undefined : maxHeight,
  });

  useEffect(() => {
    if (modalOpen) {
      setShouldRender(true);
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  useEffect(() => {
    disableScroll();
    return enableScroll;
  }, []);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && setCloseButton) {
        setCloseButton(false);
      }
    };

    window.addEventListener("keydown", handleEscKey);
    return () => {
      window.removeEventListener("keydown", handleEscKey);
    };
  }, [setCloseButton]);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    element.style.maxHeight = `${maxContentHeight}px`;
  }, [maxContentHeight]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setCloseButton?.(false);
    }, 300);
  };

  useClickOutside({
    ref: modalRef,
    setState: handleClose,
    enablesetState: closeModalOnClickOutside,
    excludedRef: carouselContainerRef,
  });
  useEscapeKey({
    setState: handleClose,
    enableSetState: closeModalOnClickOutside,
  });
  //#endregion

  if (!shouldRender) return null;

  return (
    <AnimatePresence mode="wait">
      {isAnimating && (
        <Overlay
          key={from}
          id={from}
          onDrop={onDrop && onDrop}
          onDragOver={onDrop && ((e) => e.preventDefault())}
          $zIndex={zIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          {showCarousel && (
            <CarouselContainer onClick={() => {}} ref={carouselContainerRef}>
              {carouselIndex > 0 ? (
                <Components.IconButton
                  disabled={false}
                  iconType="ChevronLeft"
                  kind="Secondary"
                  onClick={leftCarouselClick}
                  size="Small"
                  tabIndex={closeButtonTabIndex}
                />
              ) : (
                <div />
              )}
              {carouselIndex < carouselMaxLength - 1 && (
                <Components.IconButton
                  disabled={false}
                  iconType="ChevronRight"
                  kind="Secondary"
                  onClick={rightCarouselClick}
                  size="Small"
                />
              )}
            </CarouselContainer>
          )}
          <Container>
            <ModalContainer
              ref={modalRef}
              $type={type}
              $stretchToFullWidth={stretchToFullWidth}
              $stretchToFullHeight={stretchToFullHeight}
              $maxWidth={maxWidth}
              $minWidth={minWidth}
              $minHeight={minHeight}
              $maxHeight={maxHeight}
              $gap={gap}
              initial={{ y: "100vh", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100vh", opacity: 1 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                duration: 0.3,
                when: "afterChildren",
                exit: {
                  type: "tween",
                  duration: 0.3,
                  ease: "easeInOut",
                },
              }}
            >
              <ModalContext.Provider
                value={{
                  contentRef,
                  headerRef,
                  footerRef,
                  maxContentHeight,
                }}
              >
                {children}
              </ModalContext.Provider>
              {showCloseButton && (
                <CloseButtonContainer
                  $side={CloseButtonPosition}
                  className="close-button-container"
                >
                  <Components.IconButton
                    disabled={false}
                    iconType="X"
                    kind="Tertiary"
                    onClick={handleClose}
                    size={modalSize === "large" ? "Medium" : "XS"}
                    tabIndex={closeButtonTabIndex}
                  />
                </CloseButtonContainer>
              )}
            </ModalContainer>
          </Container>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

const Overlay = styled(motion.div)<{
  $zIndex: number;
}>`
  position: fixed;
  inset: 0;
  height: 100dvh;
  width: 100dvw;
  z-index: ${({ $zIndex }) => $zIndex};
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-primary-hover"].value};
  backdrop-filter: blur(4px);
`;

const Container = styled(FlexRowCenter)`
  height: 100%;
  width: 100%;
`;

const ModalContainer = styled(motion.div)<ModalProps.ModalContainer>(
  ({
    $maxHeight,
    $maxWidth,
    $minHeight,
    $minWidth,
    theme,
    $stretchToFullHeight,
    $stretchToFullWidth,
    $modalSize,
  }) => `
  position: relative;
  max-width: ${$maxWidth === "unset" ? "unset" : `${$maxWidth}px`};
  min-width: ${$minWidth === "unset" ? "unset" : `${$minWidth}px`};
  width: ${$stretchToFullWidth ? "90%" : "unset"};
  min-height: ${
    $stretchToFullHeight
      ? `${MAX_MODAL_HEIGHT}dvh`
      : $minHeight === "unset"
      ? "unset"
      : `${$minHeight}px`
  };
  max-height: ${
    $maxHeight === "unset" ? `${MAX_MODAL_HEIGHT}dvh` : `${$maxHeight}px`
  };
  padding: 0px;
  border-radius: ${
    $modalSize === "large"
      ? theme.spacing["spacing-4"].value
      : theme.spacing["spacing-2"].value
  }rem;
  border: ${theme.border_width["border-width-thin"].value}rem solid
    ${theme.Color.border["ui-border-default"].value};
  background: ${theme.Color.elevation.surface["surface-overlay"].value};
`
);
const CloseButtonContainer = styled.span<{
  $side: ModalProps.CloseButtonPosition;
}>(({ theme, $side }) => {
  let positionCss = "";
  const space = 0.5; //In rem
  const difference = parseFloat(ThisGorillaUtils.convertPxToRem("2"));
  const topSpace = -difference * space - difference;
  const sideContainsTop = $side.startsWith("top");

  switch ($side) {
    case "left":
      positionCss = `left: ${space}rem`;
      break;
    case "right":
      positionCss = `right: ${space}rem;`;
      break;
    case "top-left":
      positionCss = `top:${topSpace}rem;left: ${space}rem`;
      break;
    case "top-right":
      positionCss = `top:${topSpace}rem;right: ${space}rem`;
      break;
  }

  return `
  width: fit-content;
  position: absolute;
  top: ${space}rem;
  ${positionCss};
  background: ${
    sideContainsTop
      ? theme.Color.background.system["background-disabled"].value
      : "transparent"
  };
  z-index: ${MODAL_Z_INDEX + 1};
  pointer-events: auto;
`;
});

const CarouselContainer = styled(FlexRowSpaced)`
  position: absolute;
  width: 100%;
  top: 50%;
  z-index: ${MODAL_Z_INDEX};
  padding: 0px ${({ theme }) => theme.spacing["spacing-8"].value}px;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;
